import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
	Box,
	Stack,
	Grid,
	Col,
	Text,
	Inline,
	Alert,
	Icons,
	TextArea,
	InputGroup,
	Input,
	Select,
	Button,
	Divider,
	CheckboxInput,
	SwitchInput,
	FileInput,
} from "@sembark-travel/ui-base"
import { useField } from "react-final-form"
import { ErrorMessage } from "@sembark-travel/ui-form"
import { VariablePicker } from "./VariablePicker"
import {
	TWhatsAppRenderHtml,
	WhatsAppPreviewPanel,
} from "./WhatsAppPreviewPanel"
import {
	IWhatsAppTemplateEditorValue,
	TWhatsAppTemplateButtonType,
	TWhatsAppTemplateLanguageOption,
	TWhatsAppTemplateParameterFormat,
} from "./types"
import { insertAtCursor, setCursorPosition } from "./text-caret"
import { validateVariablePosition } from "./template-parser"
import { SelectWAFlow, SelectWAFlowScreen } from "../../Integrations"
import type {
	TTemplateVariable,
	TWAFlow,
	TWAFlowScreen,
} from "../../Integrations"
import {
	AUTHENTICATION_BUTTON_TYPE_OPTIONS,
	AUTHENTICATION_DURATION_OPTIONS,
	BUTTON_TYPE_OPTIONS,
	buildVariableErrors,
	CATEGORY_OPTIONS,
	createSampleMediaSelection,
	emptyButton,
	HEADER_TYPE_OPTIONS,
	MEDIA_MODE_OPTIONS,
	normalizeEditorForCategoryTransition,
	QUICK_REPLY_ACTION_OPTIONS,
	TTemplateVariableContext,
	updateEditorValue,
} from "./editor-model"
import { ExampleInputsBlock, UrlButtonExampleInput } from "./ExampleInputs"

export function WhatsAppTemplateEditor({
	variableContext,
	availableLanguages,
	value,
	onChange,
	onBlur,
	onFocus,
	disabled,
	renderHtml,
	waBusinessAccountId,
}: {
	variableContext: TTemplateVariableContext
	availableLanguages: TWhatsAppTemplateLanguageOption[]
	value: IWhatsAppTemplateEditorValue
	onChange: (value: IWhatsAppTemplateEditorValue) => void
	onBlur?: () => void
	onFocus?: () => void
	disabled?: boolean
	renderHtml?: TWhatsAppRenderHtml
	/**
	 * The business account the template is registered on. A Flow belongs
	 * to one account, so the Flow picker lists only this account's Flows
	 * and a picked Flow is dropped when the account changes.
	 */
	waBusinessAccountId?: number
}) {
	const headerRef = useRef<HTMLTextAreaElement>(null)
	const previousWaBusinessAccountIdRef = useRef(waBusinessAccountId)

	useEffect(() => {
		if (previousWaBusinessAccountIdRef.current === waBusinessAccountId) {
			return
		}
		previousWaBusinessAccountIdRef.current = waBusinessAccountId
		if (!value.buttons.some((b) => b.type === "flow" && b.flow_id)) {
			return
		}
		onChange({
			...value,
			buttons: value.buttons.map((b) =>
				b.type === "flow" ? { ...b, flow_id: "", flow_name: "" } : b
			),
		})
	}, [waBusinessAccountId, value, onChange])
	const bodyRef = useRef<HTMLTextAreaElement>(null)
	const footerRef = useRef<HTMLTextAreaElement>(null)
	const [activeField, setActiveField] = useState<
		"header" | "body" | "footer" | null
	>("body")
	const containerRef = useRef<HTMLDivElement>(null)

	const bodyPositionValidation = validateVariablePosition(value.body.text || "")
	const variableErrors = useMemo(
		() => buildVariableErrors(variableContext, value),
		[variableContext, value]
	)
	const wildcardPrefix = variableContext.wildcard_prefix
	const parameterFormat: TWhatsAppTemplateParameterFormat =
		value.parameter_format || "NAMED"
	const isAuthenticationTemplate = value.category === "AUTHENTICATION"
	const shouldShowAuthenticationDetails = isAuthenticationTemplate
	const availableButtonTypeOptions = isAuthenticationTemplate
		? AUTHENTICATION_BUTTON_TYPE_OPTIONS
		: BUTTON_TYPE_OPTIONS
	const availableMediaVariables = useMemo(() => {
		const expectedValueType =
			value.header.type === "video"
				? "video_url"
				: value.header.type === "document"
					? "document_url"
					: "image_url"

		return variableContext.available_parameters.filter(
			(parameter) => parameter.value_type === expectedValueType
		)
	}, [variableContext.available_parameters, value.header.type])
	const mediaTypeLabel =
		value.header.type === "video"
			? "Video"
			: value.header.type === "document"
				? "Document"
				: "Image"

	const handleInsertVariable = useCallback(
		(variable: TTemplateVariable) => {
			const textarea =
				activeField === "header"
					? headerRef.current
					: activeField === "footer"
						? footerRef.current
						: bodyRef.current

			if (!textarea) return

			const { newValue, newCursorPos } = insertAtCursor(
				textarea,
				variable.placeholder
			)

			if (activeField === "header") {
				onChange(
					updateEditorValue(value, ["header", "text"], newValue as string)
				)
			} else if (activeField === "footer") {
				onChange(updateEditorValue(value, ["footer", "text"], newValue))
			} else {
				onChange(updateEditorValue(value, ["body", "text"], newValue))
			}

			setTimeout(() => setCursorPosition(textarea, newCursorPos), 10)
		},
		[activeField, onChange, value]
	)

	return (
		<InputGroup>
			<Box ref={containerRef}>
				{!isAuthenticationTemplate && variableErrors.hasErrors ? (
					<Box marginBottom="4">
						<Alert status="warning" title="Invalid Variables">
							<Stack gap="2">
								<Text>
									The following variables are not allowed for{" "}
									<Text as="span" fontWeight="semibold">
										{variableContext.name}
									</Text>
									:
								</Text>
								<Text fontFamily="mono" fontWeight="semibold" fontSize="sm">
									{variableErrors.invalidVariables
										.map((variable) => `{{${variable}}}`)
										.join(", ")}
								</Text>
							</Stack>
						</Alert>
					</Box>
				) : null}

				<Grid borderWidth="1" rounded="lg">
					<Col xs={12} md={7} paddingX="0">
						<Stack gap="0">
							<Box
								paddingX="4"
								paddingY="3"
								borderBottomWidth="1"
								bgColor="subtle"
							>
								<Stack gap="3">
									<Text fontWeight="semibold" fontSize="sm">
										Content
									</Text>
									{isAuthenticationTemplate ? (
										<Text fontSize="sm" color="muted">
											Authentication templates use a restricted WhatsApp flow
											with no generic variables, headers, or footer.
										</Text>
									) : (
										<Stack gap="1">
											<Inline gap="2" alignItems="center" flexWrap="wrap">
												<VariablePicker
													variables={variableContext.available_parameters}
													onInsert={handleInsertVariable}
													disabled={disabled}
													containerRef={containerRef}
												/>
											</Inline>
											{wildcardPrefix ? (
												<Text fontSize="sm" color="muted">
													You can also reference custom fields as{" "}
													<code>{`{{${wildcardPrefix}<your_key>}}`}</code>.
												</Text>
											) : null}
										</Stack>
									)}
								</Stack>
							</Box>

							<Box paddingX="4" paddingY="4" borderBottomWidth="1">
								<Stack gap="4">
									<Text fontWeight="semibold" fontSize="sm">
										Template Setup
									</Text>
									<Stack gap="2">
										<Text fontWeight="semibold" fontSize="sm">
											Template Name
										</Text>
										<Input
											value={value.name}
											onChange={(e) =>
												onChange(
													updateEditorValue(
														value,
														["name"],
														e.currentTarget.value
													)
												)
											}
											onBlur={onBlur}
											onFocus={onFocus}
											placeholder="trip_created_customer_confirmation"
											disabled={disabled}
										/>
										<ErrorMessage name="editor.name" />
									</Stack>

									<Grid>
										<Col sm={12} md={6} paddingX="0">
											<Stack gap="2">
												<Text fontWeight="semibold" fontSize="sm">
													Category
												</Text>
												<Select
													labelKey="label"
													value={CATEGORY_OPTIONS.find(
														(option) => option.value === value.category
													)}
													options={CATEGORY_OPTIONS.filter(
														(option) => option.id !== "AUTHENTICATION"
													)}
													multiple={false}
													searchable={false}
													onChange={(selected) => {
														const nextCategory =
															((Array.isArray(selected)
																? selected[0]?.value
																: selected?.value) as
																| "UTILITY"
																| "MARKETING"
																| "AUTHENTICATION"
																| undefined) || "UTILITY"

														onChange(
															normalizeEditorForCategoryTransition(
																updateEditorValue(
																	value,
																	["category"],
																	nextCategory
																),
																value
															)
														)
													}}
													disabled={disabled}
												/>
												<ErrorMessage name="editor.category" />
											</Stack>
										</Col>
										<Col sm={12} md={6} paddingX="0">
											<Stack gap="2">
												<Text fontWeight="semibold" fontSize="sm">
													Language
												</Text>
												<Select
													labelKey="label"
													value={
														availableLanguages.find(
															(option) => option.code === value.language
														) || null
													}
													options={availableLanguages}
													multiple={false}
													searchable
													onChange={(selected) =>
														onChange(
															updateEditorValue(
																value,
																["language"],
																(Array.isArray(selected)
																	? selected[0]?.code
																	: selected?.code) || "en"
															)
														)
													}
													disabled={disabled}
												/>
												<ErrorMessage name="editor.language" />
											</Stack>
										</Col>
									</Grid>
								</Stack>
							</Box>

							{!isAuthenticationTemplate ? (
								<Box paddingX="4" paddingY="4" borderBottomWidth="1">
									<Stack gap="4">
										<Stack gap="2">
											<Text fontWeight="semibold" fontSize="sm">
												Header Type
											</Text>
											<Select
												labelKey="label"
												value={HEADER_TYPE_OPTIONS.find(
													(option) => option.value === value.header.type
												)}
												options={HEADER_TYPE_OPTIONS}
												multiple={false}
												searchable={false}
												onChange={(selected) => {
													const headerType =
														(Array.isArray(selected)
															? selected[0]?.value
															: selected?.value) || "none"
													let nextValue = updateEditorValue(
														value,
														["header", "type"],
														headerType
													)

													if (
														headerType === "none" ||
														headerType === "text" ||
														headerType !== value.header.type
													) {
														nextValue = updateEditorValue(
															nextValue,
															["header", "sample_media"],
															null
														)
														nextValue = updateEditorValue(
															nextValue,
															["header", "sample_media_id"],
															null
														)
													}

													onChange(nextValue)
												}}
												disabled={disabled}
											/>
											<ErrorMessage name="editor.header.type" />
										</Stack>

										{value.header.type === "text" ? (
											<Stack gap="2">
												<Text fontWeight="semibold" fontSize="sm">
													Header Text
												</Text>
												<TextArea
													ref={headerRef}
													value={value.header.text || ""}
													onChange={(e) =>
														onChange(
															updateEditorValue(
																value,
																["header", "text"],
																e.currentTarget.value
															)
														)
													}
													onFocus={() => {
														setActiveField("header")
														onFocus?.()
													}}
													onBlur={onBlur}
													rows={2}
													maxLength={60}
													disabled={disabled}
												/>
												<Inline justifyContent="between">
													<Text fontSize="xs" color="muted">
														Max 60 characters
													</Text>
													<Text fontSize="xs" color="muted">
														{value.header.text?.length || 0}/60
													</Text>
												</Inline>
												<ErrorMessage name="editor.header.text" />
												<ExampleInputsBlock
													block="header"
													text={value.header.text || ""}
													example={value.header.example}
													parameterFormat={parameterFormat}
													wildcardPrefix={wildcardPrefix}
													availableParameters={
														variableContext.available_parameters
													}
													disabled={disabled}
													onExampleChange={(nextExample) =>
														onChange({
															...value,
															header: {
																...value.header,
																example: nextExample,
															},
														})
													}
												/>
											</Stack>
										) : null}

										{value.header.type !== "none" &&
										value.header.type !== "text" ? (
											<Stack gap="2">
												<Text fontWeight="semibold" fontSize="sm">
													Sample {mediaTypeLabel}
												</Text>
												<FileInput
													accept={
														value.header.type === "video"
															? "video/*"
															: value.header.type === "document"
																? ".pdf,.doc,.docx,.txt,.ppt,.pptx,.xls,.xlsx"
																: "image/*"
													}
													onChange={(files) => {
														const file = files?.[0]
														if (!file) {
															return
														}

														onChange(
															updateEditorValue(
																value,
																["header", "sample_media"],
																createSampleMediaSelection(
																	file,
																	value.header.sample_media || null
																)
															)
														)
													}}
													onBlur={onBlur}
													disabled={disabled}
												/>
												{value.header.sample_media ? (
													<Box
														paddingX="3"
														paddingY="3"
														rounded="md"
														borderWidth="1"
														bgColor="subtle"
													>
														<Stack gap="1">
															<Text fontSize="sm" fontWeight="semibold">
																{value.header.sample_media.name ||
																	value.header.sample_media.file_name ||
																	`Sample ${mediaTypeLabel}`}
															</Text>
															<Text fontSize="xs" color="muted">
																{value.header.sample_media.mime_type ||
																	"Unknown type"}
															</Text>
														</Stack>
													</Box>
												) : null}
												<Text fontSize="xs" color="muted">
													Upload one sample {mediaTypeLabel.toLowerCase()} for
													WhatsApp template approval. We’ll reuse this uploaded
													asset for static mode.
												</Text>
												<ErrorMessage name="editor.header.sample_media" />
											</Stack>
										) : null}

										{value.header.type !== "none" &&
										value.header.type !== "text" ? (
											<Stack gap="4">
												<Stack gap="2">
													<Text fontWeight="semibold" fontSize="sm">
														{mediaTypeLabel} Mode
													</Text>
													<Select
														labelKey="label"
														value={
															MEDIA_MODE_OPTIONS.find(
																(option) =>
																	option.value ===
																	(value.header.image_mode || "static")
															) || MEDIA_MODE_OPTIONS[0]
														}
														options={MEDIA_MODE_OPTIONS.map((option) => ({
															...option,
															label:
																option.value === "static"
																	? `Static ${mediaTypeLabel}`
																	: `Dynamic ${mediaTypeLabel}`,
														}))}
														multiple={false}
														searchable={false}
														onChange={(selected) => {
															const imageMode =
																(Array.isArray(selected)
																	? selected[0]?.value
																	: selected?.value) || "static"
															let nextValue = updateEditorValue(
																value,
																["header", "image_mode"],
																imageMode
															)

															if (imageMode !== "dynamic") {
																nextValue = updateEditorValue(
																	nextValue,
																	["header", "dynamic_image_source_key"],
																	""
																)
															}

															onChange(nextValue)
														}}
														disabled={disabled}
													/>
													<ErrorMessage name="editor.header.image_mode" />
												</Stack>

												{(value.header.image_mode || "static") === "static" ? (
													<Stack gap="2">
														<Text fontWeight="semibold" fontSize="sm">
															Static {mediaTypeLabel}
														</Text>
														<Text fontSize="xs" color="muted">
															The uploaded sample {mediaTypeLabel.toLowerCase()}{" "}
															will be reused every time this template is sent.
														</Text>
													</Stack>
												) : (
													<Stack gap="2">
														<Text fontWeight="semibold" fontSize="sm">
															{mediaTypeLabel} Source Field
														</Text>
														<Select
															labelKey="label"
															value={
																availableMediaVariables.find(
																	(option) =>
																		option.key ===
																		value.header.dynamic_image_source_key
																) || null
															}
															options={availableMediaVariables}
															multiple={false}
															searchable
															onChange={(selected) =>
																onChange(
																	updateEditorValue(
																		value,
																		["header", "dynamic_image_source_key"],
																		(Array.isArray(selected)
																			? selected[0]?.key
																			: selected?.key) || ""
																	)
																)
															}
															disabled={disabled}
														/>
														{availableMediaVariables.length === 0 ? (
															<Text fontSize="xs" color="muted">
																No {mediaTypeLabel.toLowerCase()}-capable fields
																are available for the selected automation
																template.
															</Text>
														) : (
															<Text fontSize="xs" color="muted">
																WhatsApp will send the hosted{" "}
																{mediaTypeLabel.toLowerCase()} URL resolved from
																this source field.
															</Text>
														)}
														<ErrorMessage name="editor.header.dynamic_image_source_key" />
													</Stack>
												)}
											</Stack>
										) : null}
									</Stack>
								</Box>
							) : null}

							<Box paddingX="4" paddingY="4" borderBottomWidth="1">
								<Stack gap="2">
									<Text fontWeight="semibold" fontSize="sm">
										{isAuthenticationTemplate
											? "Authentication Message"
											: "Message Body"}
									</Text>
									{isAuthenticationTemplate ? (
										<Text fontSize="sm" color="muted">
											Content for authentication templates is preset by
											WhatsApp. You can only enable the supported auth options
											below.
										</Text>
									) : null}
									{isAuthenticationTemplate ? (
										<Box
											paddingX="3"
											paddingY="3"
											rounded="md"
											borderWidth="1"
											bgColor="subtle"
										>
											<Text whiteSpace="preserveLine">
												{"<VERIFICATION_CODE> is your verification code."}
											</Text>
										</Box>
									) : (
										<>
											<TextArea
												ref={bodyRef}
												value={value.body.text}
												onChange={(e) =>
													onChange(
														updateEditorValue(
															value,
															["body", "text"],
															e.currentTarget.value
														)
													)
												}
												onFocus={() => {
													setActiveField("body")
													onFocus?.()
												}}
												onBlur={onBlur}
												rows={10}
												required
												disabled={disabled}
												hasError={!bodyPositionValidation.valid}
											/>
											{!bodyPositionValidation.valid &&
											bodyPositionValidation.error ? (
												<Text fontSize="sm" color="danger">
													<Icons.Info size="3" /> {bodyPositionValidation.error}
												</Text>
											) : null}
										</>
									)}
									<ErrorMessage name="editor.body.text" />
								</Stack>
							</Box>

							{!isAuthenticationTemplate ? (
								<ExampleInputsBlock
									block="body"
									text={value.body.text || ""}
									example={value.body.example}
									parameterFormat={parameterFormat}
									wildcardPrefix={wildcardPrefix}
									availableParameters={variableContext.available_parameters}
									disabled={disabled}
									onExampleChange={(nextExample) =>
										onChange({
											...value,
											body: { ...value.body, example: nextExample },
										})
									}
								/>
							) : null}

							{!isAuthenticationTemplate ? (
								<Box paddingX="4" paddingY="4" borderBottomWidth="1">
									<Stack gap="3">
										<Inline justifyContent="between" alignItems="center">
											<Text fontWeight="semibold" fontSize="sm">
												Footer
											</Text>
											<Button
												type="button"
												size="sm"
												level="secondary"
												onClick={() =>
													onChange(
														updateEditorValue(
															value,
															["footer", "enabled"],
															!value.footer.enabled
														)
													)
												}
												disabled={disabled}
											>
												{value.footer.enabled ? "Remove Footer" : "Add Footer"}
											</Button>
										</Inline>

										{value.footer.enabled ? (
											<Stack gap="2">
												<TextArea
													ref={footerRef}
													value={value.footer.text || ""}
													onChange={(e) =>
														onChange(
															updateEditorValue(
																value,
																["footer", "text"],
																e.currentTarget.value
															)
														)
													}
													onFocus={() => {
														setActiveField("footer")
														onFocus?.()
													}}
													onBlur={onBlur}
													rows={2}
													maxLength={60}
													disabled={disabled}
												/>
												<Inline justifyContent="between">
													<Text fontSize="xs" color="muted">
														Max 60 characters
													</Text>
													<Text fontSize="xs" color="muted">
														{value.footer.text?.length || 0}/60
													</Text>
												</Inline>
												<ErrorMessage name="editor.footer.text" />
												<ExampleInputsBlock
													block="footer"
													text={value.footer.text || ""}
													example={value.footer.example}
													parameterFormat={parameterFormat}
													wildcardPrefix={wildcardPrefix}
													availableParameters={
														variableContext.available_parameters
													}
													disabled={disabled}
													onExampleChange={(nextExample) =>
														onChange({
															...value,
															footer: {
																...value.footer,
																example: nextExample,
															},
														})
													}
												/>
											</Stack>
										) : null}
									</Stack>
								</Box>
							) : null}

							<Box paddingX="4" paddingY="4" borderBottomWidth="1">
								<Stack gap="3">
									<Inline justifyContent="between" alignItems="center">
										<Text fontWeight="semibold" fontSize="sm">
											Buttons
										</Text>
										<Button
											type="button"
											size="sm"
											level="secondary"
											onClick={() =>
												onChange({
													...value,
													buttons: [
														...value.buttons,
														emptyButton(
															isAuthenticationTemplate
																? "copy_code"
																: "quick_reply"
														),
													],
												})
											}
											disabled={disabled || value.buttons.length >= 10}
										>
											<Icons.Plus /> Add Button
										</Button>
									</Inline>

									{value.buttons.length === 0 ? (
										<Text fontSize="sm" color="muted">
											{isAuthenticationTemplate
												? "Add the copy code button required for this authentication template."
												: "Add quick reply or CTA buttons if this template needs interactive actions."}
										</Text>
									) : null}

									{value.buttons.map((button, index) => (
										<Box
											key={`${button.type}-${index}`}
											borderWidth="1"
											rounded="md"
											paddingX="3"
											paddingY="3"
										>
											<Stack gap="3">
												<Inline justifyContent="between" alignItems="center">
													<Text fontWeight="semibold" fontSize="sm">
														Button {index + 1}
													</Text>
													<Button
														type="button"
														size="sm"
														level="tertiary"
														onClick={() =>
															onChange({
																...value,
																buttons: value.buttons.filter(
																	(_, itemIndex) => itemIndex !== index
																),
															})
														}
														disabled={disabled}
													>
														<Icons.Trash />
													</Button>
												</Inline>

												<Select
													labelKey="label"
													value={availableButtonTypeOptions.find(
														(option) => option.value === button.type
													)}
													options={availableButtonTypeOptions}
													multiple={false}
													searchable={false}
													onChange={(selected) => {
														const type =
															(Array.isArray(selected)
																? selected[0]?.value
																: selected?.value) ||
															(isAuthenticationTemplate
																? "copy_code"
																: "quick_reply")
														const nextButtons = [...value.buttons]
														nextButtons[index] = emptyButton(
															type as TWhatsAppTemplateButtonType
														)
														onChange({ ...value, buttons: nextButtons })
													}}
													disabled={disabled}
												/>

												{button.type === "quick_reply" ||
												button.type === "url" ||
												button.type === "phone_number" ||
												button.type === "flow" ? (
													<Stack gap="2">
														<Text fontSize="sm">Button Label</Text>
														<Input
															value={button.text || ""}
															onChange={(e) => {
																const nextButtons = [...value.buttons]
																nextButtons[index] = {
																	...nextButtons[index],
																	text: e.currentTarget.value,
																}
																onChange({ ...value, buttons: nextButtons })
															}}
															onBlur={onBlur}
															disabled={disabled}
														/>
													</Stack>
												) : null}

												{button.type === "quick_reply" ? (
													<Stack gap="2">
														<Text fontSize="sm">When Tapped</Text>
														<Select
															labelKey="label"
															value={QUICK_REPLY_ACTION_OPTIONS.find(
																(option) =>
																	option.value === (button.button_action || "")
															)}
															options={QUICK_REPLY_ACTION_OPTIONS}
															multiple={false}
															searchable={false}
															onChange={(selected) => {
																const action = Array.isArray(selected)
																	? selected[0]?.value
																	: selected?.value
																const nextButtons = [...value.buttons]
																nextButtons[index] = {
																	...nextButtons[index],
																	button_action: action || null,
																}
																onChange({ ...value, buttons: nextButtons })
															}}
															disabled={disabled}
														/>
														{button.button_action ? (
															<Text fontSize="xs" color="muted">
																Only an automation built for this can send the
																template, and it fills in what the tap is about.
															</Text>
														) : null}
													</Stack>
												) : null}

												{button.type === "url" ? (
													<Stack gap="2">
														<Text fontSize="sm">URL</Text>
														<Input
															value={button.url || ""}
															onChange={(e) => {
																const nextButtons = [...value.buttons]
																nextButtons[index] = {
																	...nextButtons[index],
																	url: e.currentTarget.value,
																}
																onChange({ ...value, buttons: nextButtons })
															}}
															onBlur={onBlur}
															disabled={disabled}
														/>
														<UrlButtonExampleInput
															url={button.url || ""}
															urlSourceKey={button.url_source_key || null}
															example={button.example || ""}
															wildcardPrefix={wildcardPrefix}
															availableParameters={
																variableContext.available_parameters
															}
															disabled={disabled}
															onExampleChange={(nextValue) => {
																const nextButtons = [...value.buttons]
																nextButtons[index] = {
																	...nextButtons[index],
																	example: nextValue || undefined,
																}
																onChange({
																	...value,
																	buttons: nextButtons,
																})
															}}
														/>
													</Stack>
												) : null}

												{button.type === "phone_number" ? (
													<Stack gap="2">
														<Text fontSize="sm">Phone Number</Text>
														<Input
															value={button.phone_number || ""}
															onChange={(e) => {
																const nextButtons = [...value.buttons]
																nextButtons[index] = {
																	...nextButtons[index],
																	phone_number: e.currentTarget.value,
																}
																onChange({ ...value, buttons: nextButtons })
															}}
															onBlur={onBlur}
															disabled={disabled}
														/>
													</Stack>
												) : null}

												{button.type === "copy_code" ? (
													<Stack gap="2">
														<Text fontSize="sm">Button Label</Text>
														<Input
															value={button.text || "Copy code"}
															onChange={(e) => {
																const nextButtons = [...value.buttons]
																nextButtons[index] = {
																	...nextButtons[index],
																	text: e.currentTarget.value,
																}
																onChange({ ...value, buttons: nextButtons })
															}}
															onBlur={onBlur}
															disabled={disabled}
														/>
														<Text fontSize="xs" color="muted">
															This creates an OTP copy-code button for
															authentication templates.
														</Text>
													</Stack>
												) : null}

												{button.type === "flow" ? (
													<Stack gap="2">
														<Text fontSize="sm">Flow</Text>
														<SelectWAFlow
															value={
																button.flow_id
																	? {
																			id: button.flow_id,
																			name:
																				button.flow_name ||
																				`Flow #${button.flow_id}`,
																			status: "PUBLISHED",
																		}
																	: null
															}
															onChange={(selected: unknown) => {
																const flow = (
																	Array.isArray(selected)
																		? selected[0]
																		: selected
																) as TWAFlow | null
																const nextButtons = [...value.buttons]
																// A screen belongs to one Flow, so a new Flow
																// starts with no screen; the picker below then
																// selects the new Flow's first.
																nextButtons[index] = {
																	...nextButtons[index],
																	flow_id: flow ? String(flow.id) : undefined,
																	flow_name: flow?.name || "",
																	flow_screen: "",
																}
																onChange({ ...value, buttons: nextButtons })
															}}
															waBusinessAccountId={waBusinessAccountId}
															placeholder={
																waBusinessAccountId
																	? "Select a published Flow"
																	: "Pick the WA business account first"
															}
															disabled={disabled || !waBusinessAccountId}
														/>
														<Text fontSize="xs" color="muted">
															Opens a form the customer fills in. Only Flows
															published on the template&apos;s business account
															are listed; one Flow button per template. A
															submitted form can start an automation.
														</Text>
														<Text fontSize="sm">Opening Screen</Text>
														<SelectWAFlowScreen
															key={`${waBusinessAccountId || ""}:${button.flow_id || ""}`}
															flowId={button.flow_id}
															waBusinessAccountId={waBusinessAccountId}
															autoSelectFirst
															value={
																button.flow_screen
																	? {
																			id: button.flow_screen,
																			title: button.flow_screen,
																		}
																	: null
															}
															onChange={(selected: unknown) => {
																const screen = (
																	Array.isArray(selected)
																		? selected[0]
																		: selected
																) as TWAFlowScreen | null
																const nextButtons = [...value.buttons]
																nextButtons[index] = {
																	...nextButtons[index],
																	flow_screen: screen?.id || "",
																}
																onChange({ ...value, buttons: nextButtons })
															}}
															placeholder={
																button.flow_id
																	? "Select the opening screen"
																	: "Pick the Flow first"
															}
															disabled={disabled || !button.flow_id}
														/>
														<Text fontSize="xs" color="muted">
															The screen the form opens on. Meta requires one;
															the Flow&apos;s first screen is picked for you.
														</Text>
													</Stack>
												) : null}
											</Stack>
										</Box>
									))}
									<ErrorMessage name="editor.buttons" />
								</Stack>
							</Box>

							{shouldShowAuthenticationDetails ? (
								<Box paddingX="4" paddingY="4">
									<Stack gap="3">
										<Text fontWeight="semibold" fontSize="sm">
											Authentication Details
										</Text>
										<Text fontSize="sm" color="muted">
											Content for authentication message templates can&apos;t be
											edited. You can add additional content from the options
											below.
										</Text>

										<Stack gap="3">
											<Box as="label">
												<Inline gap="2" alignItems="center">
													<CheckboxInput
														checked={
															value.authentication.security_recommendation
														}
														onChange={() =>
															onChange(
																updateEditorValue(
																	value,
																	["authentication", "security_recommendation"],
																	!value.authentication.security_recommendation
																)
															)
														}
														disabled={disabled}
													/>
													<Text>Add security recommendation</Text>
												</Inline>
											</Box>

											<Box as="label">
												<Inline gap="2" alignItems="center">
													<CheckboxInput
														checked={
															value.authentication.code_expiration_enabled
														}
														onChange={() =>
															onChange(
																updateEditorValue(
																	value,
																	["authentication", "code_expiration_enabled"],
																	!value.authentication.code_expiration_enabled
																)
															)
														}
														disabled={disabled}
													/>
													<Text>Add expiration time for the code</Text>
												</Inline>
											</Box>
										</Stack>

										{value.authentication.code_expiration_enabled ? (
											<Box
												paddingX="3"
												paddingY="3"
												rounded="md"
												borderWidth="1"
												bgColor="subtle"
											>
												<Stack gap="2">
													<Text fontWeight="semibold" fontSize="sm">
														Expires in
													</Text>
													<Select
														labelKey="label"
														value={
															AUTHENTICATION_DURATION_OPTIONS.find(
																(option) =>
																	option.value ===
																	value.authentication.code_expiration_minutes
															) || null
														}
														options={AUTHENTICATION_DURATION_OPTIONS}
														multiple={false}
														searchable={false}
														onChange={(selected) =>
															onChange(
																updateEditorValue(
																	value,
																	["authentication", "code_expiration_minutes"],
																	(Array.isArray(selected)
																		? selected[0]?.value
																		: selected?.value) || 5
																)
															)
														}
														disabled={disabled}
													/>
												</Stack>
											</Box>
										) : null}

										<Box paddingX="3" paddingY="3" rounded="md" borderWidth="1">
											<Stack gap="2">
												<Text fontWeight="semibold" fontSize="sm">
													Message validity period
												</Text>
												<Text fontSize="sm" color="muted">
													If you don&apos;t set a custom validity period, the
													standard 10 minutes WhatsApp message validity period
													will be applied.
												</Text>
												<Box as="label">
													<Inline gap="2" alignItems="center">
														<SwitchInput
															checked={
																value.authentication.message_validity_enabled
															}
															onChange={() =>
																onChange(
																	updateEditorValue(
																		value,
																		[
																			"authentication",
																			"message_validity_enabled",
																		],
																		!value.authentication
																			.message_validity_enabled
																	)
																)
															}
															disabled={disabled}
														/>
														<Text>
															Set custom validity period for your message
														</Text>
													</Inline>
												</Box>
												{!value.authentication.message_validity_enabled ? (
													<Text fontSize="xs" color="muted">
														Using standard WhatsApp validity period: 10 minutes
													</Text>
												) : null}
												{value.authentication.message_validity_enabled ? (
													<Select
														labelKey="label"
														value={
															AUTHENTICATION_DURATION_OPTIONS.find(
																(option) =>
																	option.value * 60 ===
																	value.authentication.message_validity_seconds
															) || null
														}
														options={AUTHENTICATION_DURATION_OPTIONS}
														multiple={false}
														searchable={false}
														onChange={(selected) =>
															onChange(
																updateEditorValue(
																	value,
																	[
																		"authentication",
																		"message_validity_seconds",
																	],
																	((Array.isArray(selected)
																		? selected[0]?.value
																		: selected?.value) || 10) * 60
																)
															)
														}
														disabled={disabled}
													/>
												) : null}
											</Stack>
										</Box>
										<ErrorMessage name="editor.authentication.code_expiration_minutes" />
										<ErrorMessage name="editor.authentication.message_validity_seconds" />
									</Stack>
								</Box>
							) : null}
						</Stack>
					</Col>

					<Col
						paddingX="0"
						xs={12}
						md={5}
						borderLeftWidth={{ xs: "0", md: "1" }}
						borderTopWidth={{ xs: "1", md: "0" }}
					>
						<WhatsAppPreviewPanel value={value} renderHtml={renderHtml} />
					</Col>
				</Grid>
			</Box>
		</InputGroup>
	)
}

export function WhatsAppTemplateInputField({
	name,
	variableContext,
	availableLanguages,
	onValueChange,
	disabled,
	renderHtml,
	waBusinessAccountId,
}: {
	name: string
	variableContext: TTemplateVariableContext
	availableLanguages: TWhatsAppTemplateLanguageOption[]
	onValueChange?: (value: IWhatsAppTemplateEditorValue) => void
	disabled?: boolean
	renderHtml?: TWhatsAppRenderHtml
	waBusinessAccountId?: number
}) {
	const {
		input: { value, onChange, onBlur, onFocus },
	} = useField<IWhatsAppTemplateEditorValue>(name, {
		subscription: {
			value: true,
		},
	})

	return (
		<Stack gap="1">
			<WhatsAppTemplateEditor
				value={value}
				onChange={(nextValue) => {
					onChange(nextValue)
					onValueChange?.(nextValue)
				}}
				onBlur={onBlur}
				onFocus={onFocus}
				variableContext={variableContext}
				availableLanguages={availableLanguages}
				disabled={disabled}
				renderHtml={renderHtml}
				waBusinessAccountId={waBusinessAccountId}
			/>
			<Divider sm />
			<ErrorMessage name={`${name}.name`} />
			<ErrorMessage name={`${name}.category`} />
			<ErrorMessage name={`${name}.language`} />
		</Stack>
	)
}
